// Servicio para gestionar la fase de subasta abierta
// Incluye migración de ofertas ciegas a bids cuando se activa la fase OPEN

import { supabase } from '../../supabaseClient';

/**
 * Migra ofertas ciegas seleccionadas a la tabla de bids
 * Esto permite que los usuarios seleccionados vean sus ofertas ciegas como pujas activas
 *
 * @param {Array} selectedOffers - Array de ofertas ciegas con estructura:
 *   { id, amount, buyerName, buyerEmail, conditionsText, createdAt, inviteId }
 * @param {string} propertyId - ID de la propiedad
 * @returns {Promise<number>} - Número de bids creados
 */
export const migrateBlindOffersToBids = async (selectedOffers, propertyId) => {
  try {
    console.log('🔄 Migrando ofertas ciegas a bids:', {
      propertyId,
      offersCount: selectedOffers.length
    });

    if (!selectedOffers || selectedOffers.length === 0) {
      console.log('⚠️ No hay ofertas para migrar');
      return 0;
    }

    // Construir array de bids para insertar
    const bidsToInsert = selectedOffers.map(offer => ({
      property_id: propertyId,
      buyer_name: offer.buyerName,
      buyer_email: offer.buyerEmail,
      amount: offer.amount,
      conditions_text: offer.conditionsText || null,
      bid_time: offer.createdAt, // Mantener fecha original de la oferta ciega
      is_active: true,
      is_last_chance: false,
    }));

    console.log('📦 Bids a insertar:', bidsToInsert);

    // Insertar todas las bids en una sola operación
    const { data, error } = await supabase
      .from('bids')
      .insert(bidsToInsert)
      .select();

    if (error) {
      console.error('❌ Error insertando bids:', error);
      throw error;
    }

    console.log(`✅ ${data.length} ofertas migradas exitosamente a bids`);
    return data.length;
  } catch (error) {
    console.error('❌ Error en migrateBlindOffersToBids:', error);
    throw error;
  }
};

/**
 * Marca usuarios como finalistas en la tabla invites
 * Solo los finalistas pueden acceder a la fase OPEN
 *
 * @param {Array<string>} inviteIds - IDs de las invitaciones seleccionadas
 * @returns {Promise<void>}
 */
export const markFinalists = async (inviteIds) => {
  try {
    if (!inviteIds || inviteIds.length === 0) {
      console.log('⚠️ No hay finalistas para marcar');
      return;
    }

    console.log('🏆 Marcando finalistas:', inviteIds);

    // Primero, desmarcar TODOS los finalistas de esta propiedad
    // (por si se está re-activando la subasta)
    // Esto requeriría el propertyId, así que lo omitimos por ahora
    // Si es necesario, se puede agregar después

    // Marcar los nuevos finalistas
    const { error } = await supabase
      .from('invites')
      .update({ has_access: true })
      .in('id', inviteIds);

    if (error) {
      console.error('❌ Error marcando finalistas:', error);
      throw error;
    }

    console.log(`✅ ${inviteIds.length} finalistas marcados correctamente`);
  } catch (error) {
    console.error('❌ Error en markFinalists:', error);
    throw error;
  }
};

/**
 * Valida que un usuario tenga acceso a la fase OPEN
 * Solo los finalistas (has_access = true) pueden acceder
 *
 * @param {string} propertyId - ID de la propiedad
 * @param {string} token - Token de invitación
 * @returns {Promise<boolean>} - true si tiene acceso, false si no
 */
export const validateOpenAuctionAccess = async (propertyId, token) => {
  try {
    const { data, error } = await supabase
      .from('invites')
      .select('has_access')
      .eq('property_id', propertyId)
      .eq('token', token)
      .single();

    if (error) {
      console.error('Error validando acceso:', error);
      return false;
    }

    return data?.has_access === true;
  } catch (error) {
    console.error('Error en validateOpenAuctionAccess:', error);
    return false;
  }
};
